import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapCanvas from '../map/MapCanvas.jsx';
import PlacePicker from '../components/PlacePicker.jsx';
import { useToast } from '../components/Toast.jsx';
import { api } from '../lib/api.js';
import { fromLocalInput, toLocalInput } from '../lib/format.js';
import { getRecent, rememberAdmin, rememberMember, setDisplayName } from '../lib/identity.js';
import { navigate } from '../lib/router.js';

/** Next Saturday at 07:00 — by far the most common club departure. */
function defaultDeparture() {
  const d = new Date();
  d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7 || 7));
  d.setHours(7, 0, 0, 0);
  return d.toISOString();
}

export default function Landing({ meta }) {
  const toast = useToast();
  const [form, setForm] = useState({
    title: '',
    organiser: '',
    destination: null,
    meetingPoint: null,
    departureDate: toLocalInput(defaultDeparture()),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [openLink, setOpenLink] = useState('');
  /**
   * The map on this form is a thumbnail, which is fine for confirming a pin and
   * useless for navigating. It grows the moment it is used and shrinks again as
   * soon as attention returns to the form, so the fields are never pushed off
   * screen by a map nobody is looking at.
   */
  const [mapExpanded, setMapExpanded] = useState(false);
  const mapBoxRef = useRef(null);

  const expandMap = useCallback(() => {
    setMapExpanded(true);
    // Growing downwards can push the map out of view on a short screen.
    requestAnimationFrame(() => mapBoxRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  }, []);
  const recent = useMemo(() => getRecent(), []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const markers = useMemo(() => {
    const out = [];
    if (form.destination?.lat != null) {
      out.push({
        id: 'dest',
        kind: 'dest',
        glyph: '🏔',
        lat: form.destination.lat,
        lng: form.destination.lng,
        label: form.destination.label,
        signature: `d:${form.destination.lat}`,
      });
    }
    if (form.meetingPoint?.lat != null) {
      out.push({
        id: 'meet',
        kind: 'meet',
        glyph: '🅿',
        lat: form.meetingPoint.lat,
        lng: form.meetingPoint.lng,
        label: form.meetingPoint.label,
        signature: `m:${form.meetingPoint.lat}`,
      });
    }
    return out;
  }, [form.destination, form.meetingPoint]);

  const fitBounds = markers.length ? markers.map((m) => [m.lat, m.lng]) : null;
  const fitKey = markers.map((m) => m.signature).join('|');

  const submit = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.destination || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.createTrip({
        title: form.title.trim(),
        organiser: form.organiser.trim(),
        destination: form.destination,
        meetingPoint: form.meetingPoint,
        departureDate: fromLocalInput(form.departureDate),
      });
      rememberAdmin(res.shareToken, res.adminToken);
      // The organiser is enrolled server-side, so they are never asked who they are.
      if (res.memberId) rememberMember(res.shareToken, res.memberId);
      if (form.organiser.trim()) setDisplayName(form.organiser.trim());
      toast.success('Sortie creee. Partagez le lien au club.');
      navigate(`/t/${res.shareToken}`);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="landing">
      <div className="landing-inner">
        <div className="brand">
          <span className="brand-mark">🧗</span>
          <span>Covoiturage</span>
        </div>

        <h1 className="hero-title">Une carte, un lien, et le covoiturage est regle.</h1>
        <p className="hero-sub">
          Creez la sortie, partagez le lien au club. Chacun ajoute sa voiture, ses arrets et ses places
          libres, ou reserve un siege. Sans compte, sans inscription.
        </p>

        <form
          className="card stack gap-16"
          style={{ marginTop: 26 }}
          onSubmit={submit}
        >
          {error && <div className="toast error" style={{ borderRadius: 10 }}>⚠ <span>{error}</span></div>}

          <div className="field">
            <label htmlFor="l-title">Nom de la sortie *</label>
            <input
              id="l-title"
              className="input"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="Sortie Ceuse - secteur Cascade"
              maxLength={120}
              required
            />
          </div>

          <PlacePicker
            label="Destination"
            required
            value={form.destination}
            onChange={(destination) => set({ destination })}
            placeholder="Parking du site, falaise, refuge..."
                hint="Le point de convergence de toutes les voitures."
          />


          <PlacePicker
            label="Rendez-vous avant le depart"
            value={form.meetingPoint}
            onChange={(meetingPoint) => set({ meetingPoint })}
            placeholder="Local du club, parking de regroupement..."
            tiles={meta?.tiles}
            hint="Optionnel."
          />

          <div className="field-row">
            <div className="field">
              <label htmlFor="l-date">Depart *</label>
              <input
                id="l-date"
                className="input"
                type="datetime-local"
                value={form.departureDate}
                onChange={(e) => set({ departureDate: e.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="l-org">Votre nom</label>
              <input
                id="l-org"
                className="input"
                value={form.organiser}
                onChange={(e) => set({ organiser: e.target.value })}
                placeholder="Organisateur"
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={busy || !form.title.trim() || !form.destination}>
            {busy && <span className="spinner" />}
            Creer la sortie et obtenir le lien
          </button>
        </form>

        {recent.length > 0 && (
          <div style={{ marginTop: 26 }}>
            <div className="section-label">Sorties recentes sur ce navigateur</div>
            <div className="stack gap-8">
              {recent.map((r) => (
                <button
                  key={r.shareToken}
                  type="button"
                  className="btn"
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() => navigate(`/t/${r.shareToken}`)}
                >
                  🧗 <span className="truncate">{r.title || 'Sortie'}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="steps">
          <div className="section-label" style={{ marginTop: 12 }}>Comment ca marche</div>
          <div className="step">
            <span className="step-num">1</span>
            <p className="small muted">
              Vous creez la sortie et recevez un lien prive, impossible a deviner, comme un album photo partage.
            </p>
          </div>
          <div className="step">
            <span className="step-num">2</span>
            <p className="small muted">
              Chaque conducteur ouvre le lien, donne son nom, son point de depart, ses arrets et le nombre de
              places passager reellement disponibles.
            </p>
          </div>
          <div className="step">
            <span className="step-num">3</span>
            <p className="small muted">
              Les autres voient tous les itineraires sur la carte et reservent la place qui leur convient. Tout
              le monde voit les changements en direct.
            </p>
          </div>
        </div>

        <div className="card" style={{ marginTop: 22, background: 'var(--surface)' }}>
          <div className="section-label">Deja un lien ?</div>
          <p className="small muted" style={{ marginBottom: 10 }}>
            Collez-le ici, ou ouvrez-le directement. Il n'y a rien a installer.
          </p>
          <form
            className="row gap-8"
            onSubmit={(e) => {
              e.preventDefault();
              const match = openLink.trim().match(/\/t\/([A-Za-z0-9_-]{16,64})/);
              if (match) navigate(`/t/${match[1]}`);
              else toast.error('Ce lien ne ressemble pas a un lien de sortie.');
            }}
          >
            <input
              className="input"
              value={openLink}
              onChange={(e) => setOpenLink(e.target.value)}
              placeholder={`${window.location.origin}/t/...`}
              aria-label="Lien de la sortie"
            />
            <button type="submit" className="btn" disabled={!openLink.trim()}>Ouvrir</button>
          </form>
        </div>

        <p className="small faint" style={{ marginTop: 26, textAlign: 'center' }}>
          Fond de carte <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · itineraires{' '}
          <a href="https://project-osrm.org/">OSRM</a> · geocodage{' '}
          <a href="https://nominatim.org/">Nominatim</a>
        </p>
      </div>
    </div>
  );
}
